import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { getLoginUrl } from "@/const";
import { useLanguage } from "@/contexts/LanguageContext";
import { BriefcaseBusiness, UserRound } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ContainedBadge } from "@/components/ContainedBadge";
import { RouteFeedback } from "@/components/RouteFeedback";

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/98466279/N8L9TKRXjtLMpzosC4Ao5k/mizano-icon-exact-192_e336f133.png";

export default function SignIn() {
  const { user, loading } = useAuth();
  const userId = user?.id;
  const { language, setLanguage, t, isRTL } = useLanguage();
  const [, setLocation] = useLocation();
  const isTemporaryPreview = typeof window !== "undefined" && window.location.hostname.endsWith(".manus.computer");
  const [workspace, setWorkspace] = useState<"individual" | "business">("individual");

  useEffect(() => {
    document.title = language === "ar" ? "تسجيل الدخول | ميزانو" : "Sign in | Mizano";
  }, [language]);

  useEffect(() => {
    if (!loading && userId) setLocation("/dashboard", { replace: true });
  }, [loading, setLocation, userId]);

  if (loading) return <div className="min-h-screen min-h-[100dvh] bg-background"><RouteFeedback isLoading onRetry={() => window.location.reload()} /></div>;
  if (user) return <div className="min-h-screen min-h-[100dvh] bg-background"><RouteFeedback isLoading onRetry={() => window.location.reload()} /></div>;

  const switchLanguage = () => setLanguage(language === "en" ? "ar" : "en");

  return (
    <div className="min-h-[100dvh] bg-background text-foreground" dir={isRTL ? "rtl" : "ltr"}>
      <header className="border-b border-border bg-background/90 backdrop-blur-xl safe-area-top">
        <div className="mx-auto flex h-16 w-full max-w-[1120px] items-center px-5 sm:px-8 lg:px-16">
          <a href="/" className="flex items-center gap-2.5 font-display text-[1.05rem] font-semibold tracking-[-.02em]" aria-label={language === "ar" ? "العودة إلى الصفحة الرئيسية" : "Back to Mizano home"}>
            <img src={LOGO_URL} alt="" className="h-7 w-7 rounded-lg" />
            <span>Mizano</span>
          </a>
          <button onClick={switchLanguage} className="tap-target ms-auto px-1.5 text-[.85rem] text-muted-foreground transition-colors hover:text-foreground" aria-label={language === "en" ? "Switch to Arabic" : "Switch to English"}>
            {language === "en" ? "عربي" : "EN"}
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[400px] px-4 py-8 sm:px-6 sm:py-14" data-testid="signin-flow">
        <section className="w-full max-w-[400px] rounded-2xl border border-border bg-card p-6 shadow-[0_24px_64px_-36px_rgba(0,0,0,.7)] sm:p-9" aria-labelledby="signin-heading">
          <div className="flex flex-col items-center text-center">
            <img src={LOGO_URL} alt="Mizano" className="h-16 w-16 rounded-2xl" />
            <h1 id="signin-heading" className="mt-5 font-display text-xl font-semibold tracking-tight sm:text-2xl">{t("app.subtitle")}</h1>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">{t("app.signInDesc")}</p>
          </div>

          {isTemporaryPreview && (
            <p className="mt-6 rounded-xl border border-border bg-secondary px-3 py-3 text-center text-xs leading-relaxed text-muted-foreground">
              {language === "ar"
                ? "ستُكمل عملية تسجيل الدخول بأمان على mizanoapp.com، ثم ستُعاد إلى الموقع المباشر."
                : "To sign in securely, you will continue on mizanoapp.com and return to the live site."}
            </p>
          )}

          <fieldset className="mt-7" aria-label={t("workspace.choose")}>
            <legend className="mb-3 text-sm font-medium text-foreground">{t("workspace.choose")}</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => setWorkspace("individual")} className={`min-h-28 rounded-xl border p-4 text-start transition-colors ${workspace === "individual" ? "border-primary bg-primary/10 ring-1 ring-primary" : "border-border bg-surface hover:border-primary/60"}`} aria-pressed={workspace === "individual"}>
                <UserRound className={`mb-3 h-5 w-5 ${workspace === "individual" ? "text-primary" : "text-muted-foreground"}`} />
                <span className="block font-semibold">{t("workspace.individual")}</span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{t("workspace.individualDescription")}</span>
              </button>
              <button type="button" disabled className="min-h-28 cursor-not-allowed rounded-xl border border-border bg-muted/40 p-4 text-start opacity-65" aria-disabled="true">
                <BriefcaseBusiness className="mb-3 h-5 w-5 text-muted-foreground" />
                <span className="flex min-w-0 flex-wrap items-center gap-2"><span className="font-semibold">{t("workspace.business")}</span><ContainedBadge>{t("workspace.inDevelopment")}</ContainedBadge></span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">{t("workspace.businessDescription")}</span>
              </button>
            </div>
          </fieldset>

          <Button onClick={() => { window.location.href = getLoginUrl(workspace); }} className="mt-5 min-h-12 w-full bg-[#3B82F6] text-base font-medium text-[#04101F] shadow-[0_12px_28px_-14px_#3B82F6] transition-colors hover:bg-[#5C9AFF]">
            {t("workspace.continueIndividual")}
          </Button>

          <p className="mt-5 text-center text-sm text-muted-foreground">
            {language === "ar" ? "جديد على ميزانو؟ " : "New to Mizano? "}
            <a href="/" className="font-medium text-primary transition-colors hover:text-primary/80">{language === "ar" ? "تعرّف على طريقة عمله" : "See how it works"}</a>
          </p>
        </section>
      </main>

      <footer className="safe-area-bottom px-4 pb-6 text-center sm:px-6 sm:pb-8">
        <div className="flex items-center justify-center gap-3 text-xs text-faint">
          <a href="/privacy" className="flex min-h-[44px] items-center transition-colors hover:text-foreground">{language === "ar" ? "سياسة الخصوصية" : "Privacy Policy"}</a>
          <span aria-hidden="true">•</span>
          <a href="/terms" className="flex min-h-[44px] items-center transition-colors hover:text-foreground">{language === "ar" ? "شروط الخدمة" : "Terms of Service"}</a>
        </div>
      </footer>
    </div>
  );
}
